import * as React from "react";

import { useSocialCoachSession } from "@/utils";
import { EmptyView } from "@/components/EmptyView";
import { Combobox, Spinner } from "@/components/ui";
import * as DTO from "../../../../interfaces";
import { VideoProjectServices } from "@/services";
import { VideoProject, VideoProjectServices as BedrockVideoProjectServices } from "@/services/bedrock";

import VideoProjectsList from "./VideoProjectsList";
import VideoProjectsSearchList from "./VideoProjectsSearchList";

type VideoProjectStatusOption = {
  key: number;
  label: string;
  value: VideoProject.Status;
};

interface VideoProjectsLegacyProps {
  experienceToggle: React.ReactNode;
  sharedActions: React.ReactNode;
}

export function VideoProjectsLegacy({ experienceToggle, sharedActions }: VideoProjectsLegacyProps) {
  const { coachId } = useSocialCoachSession();
  const [loadingPlayers, setLoadingPlayers] = React.useState(false);
  const [loadingVideoProjects, setLoadingVideoProjects] = React.useState(false);
  const [searchedVideoProjectsList, setSearchedVideoProjectsList] = React.useState<VideoProject[]>([]);
  const [players, setPlayers] = React.useState<DTO.PlayerResumeResponse[]>([]);
  const [selectedStatus, setSelectedStatus] = React.useState<VideoProjectStatusOption>();
  const statuses: VideoProjectStatusOption[] = VideoProject.FilterableStatuses.map((status, index) => ({
    key: index,
    label: VideoProject.StatusMeta[status].label,
    value: status,
  }));

  React.useEffect(() => {
    if (coachId) {
      setLoadingPlayers(true);
      VideoProjectServices.getPlayersWithVideoProjects(coachId)
        .then(result => {
          setPlayers(result);
        })
        .finally(() => {
          setLoadingPlayers(false);
        });
    }
  }, [coachId]);

  React.useEffect(() => {
    if (!selectedStatus || !coachId) {
      return;
    }

    setLoadingVideoProjects(true);
    const status = VideoProject.statusesForFilter([selectedStatus.value]);

    BedrockVideoProjectServices.index({
      status,
      page: 1,
      perPage: 50,
      sort: "created_date",
      order: "desc",
      extraFields: ["needsAck"],
    })
      .then(response => {
        setSearchedVideoProjectsList(response.content);
      })
      .finally(() => {
        setLoadingVideoProjects(false);
      });
  }, [selectedStatus, coachId]);

  const content = React.useMemo(() => {
    if (loadingVideoProjects || loadingPlayers) {
      return <Spinner className="size-12 self-center mt-25" />;
    } else if (players.length === 0) {
      return <EmptyView text="No Video Projects" />;
    } else if (selectedStatus) {
      const playerVideoProjectComponents = players
        .map(player => {
          const videoProjects = searchedVideoProjectsList.filter(vp => vp.playerId === player.playerId);
          if (videoProjects.length === 0) return undefined;
          return (
            <VideoProjectsSearchList
              key={"player_video_project_section_" + player.playerId}
              player={player}
              videoProjects={videoProjects}
            />
          );
        })
        .filter(component => component !== undefined);
      if (playerVideoProjectComponents.length === 0) {
        return <EmptyView text="No video projects found for the selected status" />;
      } else {
        return playerVideoProjectComponents;
      }
    } else {
      return players.map(player => (
        <VideoProjectsList key={"video_catalyst_list_c_" + player.playerId} player={player} />
      ));
    }
  }, [searchedVideoProjectsList, players, selectedStatus, loadingVideoProjects, loadingPlayers]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex gap-4 items-center flex-wrap justify-between">
        <div className="flex gap-4 items-center flex-wrap flex-auto">
          <Combobox
            className="max-w-80 min-w-50"
            items={statuses}
            value={selectedStatus}
            placeholder="Filter By Status"
            showClear
            onValueChange={value => setSelectedStatus(value || undefined)}
          />
          {experienceToggle}
        </div>
        {sharedActions}
      </div>
      {content}
    </div>
  );
}
